import { BaseFeature } from '../../core/base-feature.js';

/**
 * ConsentFeature - Frontend window into the desktop's time-gated consent
 *
 * The desktop owns all consent state. From here the partner can:
 *   - read the current consent status (window, days remaining, grace, etc.)
 *   - provision/rotate the partner-held revoke code (stored hashed on the
 *     desktop; the plaintext is never persisted anywhere)
 *   - nudge the desktop to show its consent prompt (e.g. to request renewal)
 *
 * The partner CANNOT grant, renew, revoke, or cancel a self-release from here —
 * those actions only exist on the desktop's own dialogs. Status changes also
 * arrive as 'status_update' pushes and as consent.* Sacred Timeline events.
 */
export class ConsentFeature extends BaseFeature {
    constructor(coreAPI) {
        super(coreAPI);
        this.pendingRequests = new Map();
        this.currentStatus = null;
    }

    static getMetadata() {
        return {
            id: 'consent',
            name: 'Monitoring Consent',
            version: 'v1.0',
            capabilities: ['get_status', 'set_revoke_code', 'prompt_consent'],
            dependencies: []
        };
    }

    async initialize() {
        console.log('[Consent] Initialized');
    }

    async handleMessage(message) {
        const { action, cmd, payload } = message;
        const command = cmd || action;

        switch (command) {
            case 'status_update':
                // Consent is per-partner. The desktop's push is a whole-consent
                // summary; re-fetch OUR OWN scoped state (the desktop resolves it
                // from our peer identity) so the panel shows this partner's window.
                this.getStatus().then((res) => {
                    if (res?.success && res.state) {
                        this.currentStatus = res.state;
                        this.emit('statusUpdate', res.state);
                    }
                }).catch(() => { /* a consent.* timeline event will refresh us anyway */ });
                break;

            case 'get_status_response':
            case 'set_revoke_code_response':
            case 'prompt_consent_response': {
                const originalId = payload?.originalMessageId;
                if (originalId && this.pendingRequests.has(originalId)) {
                    const { resolve } = this.pendingRequests.get(originalId);
                    this.pendingRequests.delete(originalId);
                    resolve(payload);
                }
                break;
            }

            default:
                console.warn(`[Consent] Unknown command: ${command}`);
        }
    }

    _sendRequest(action, payload = {}) {
        return new Promise((resolve, reject) => {
            const messageId = this.sendMessage(action, payload);
            const timeout = setTimeout(() => {
                this.pendingRequests.delete(messageId);
                reject(new Error(`Request '${action}' timed out`));
            }, 10000);

            this.pendingRequests.set(messageId, {
                resolve: (result) => {
                    clearTimeout(timeout);
                    resolve(result);
                },
                reject
            });
        });
    }

    getStatus() {
        return this._sendRequest('get_status');
    }

    /**
     * Provision (or rotate) the partner-held revoke code. The desktop stores
     * only a salted hash. Rotation requires the current code.
     */
    setRevokeCode(code, currentCode) {
        const payload = { code };
        if (currentCode) payload.currentCode = currentCode;
        return this._sendRequest('set_revoke_code', payload);
    }

    /** Ask the desktop to show its consent prompt (grant or renew). */
    promptConsent(mode = 'grant') {
        return this._sendRequest('prompt_consent', { mode });
    }

    async cleanup() {
        for (const [, { reject }] of this.pendingRequests) {
            reject(new Error('Feature cleanup'));
        }
        this.pendingRequests.clear();
    }
}
